import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'weight', 'weightUnit', 'unitImperial', 'unitMetric',
    'activity', 'climate', 'exerciseDuration', 'exerciseIntensity',
    'caffeine', 'alcohol', 'pregnant', 'illness',
    'resultContainer', 'totalOunces', 'primaryUnit', 'alternateUnit',
    'glassCount', 'glass1', 'glass2', 'glass3', 'glass4',
    'morningAmount', 'exerciseAmount', 'hourlyAmount',
    'preWorkout', 'duringWorkout', 'postWorkout',
    'insight'
  ]

  connect() {
    this.isMetric = false
    this.calculate()
  }

  setImperial() {
    if (!this.isMetric) return
    this.isMetric = false
    this.updateUnitDisplay()
    this.convertToImperial()
    this.calculate()
  }

  setMetric() {
    if (this.isMetric) return
    this.isMetric = true
    this.updateUnitDisplay()
    this.convertToMetric()
    this.calculate()
  }

  updateUnitDisplay() {
    if (this.isMetric) {
      this.unitMetricTarget.classList.add('bg-gray-900', 'text-white')
      this.unitMetricTarget.classList.remove('bg-white', 'text-gray-900')
      this.unitImperialTarget.classList.remove('bg-gray-900', 'text-white')
      this.unitImperialTarget.classList.add('bg-white', 'text-gray-900')
      this.weightUnitTarget.textContent = 'kg'
    } else {
      this.unitImperialTarget.classList.add('bg-gray-900', 'text-white')
      this.unitImperialTarget.classList.remove('bg-white', 'text-gray-900')
      this.unitMetricTarget.classList.remove('bg-gray-900', 'text-white')
      this.unitMetricTarget.classList.add('bg-white', 'text-gray-900')
      this.weightUnitTarget.textContent = 'lbs'
    }
  }

  convertToMetric() {
    const lbs = parseFloat(this.weightTarget.value) || 165
    this.weightTarget.value = Math.round(lbs * 0.453592)
  }

  convertToImperial() {
    const kg = parseFloat(this.weightTarget.value) || 75
    this.weightTarget.value = Math.round(kg / 0.453592)
  }

  calculate() {
    // Get weight in lbs for calculation
    let weightLbs = parseFloat(this.weightTarget.value) || 165
    if (this.isMetric) {
      weightLbs /= 0.453592
    }

    // Base calculation: 0.5-1 oz per lb of body weight (we use 0.67 as middle ground)
    let baseOunces = weightLbs * 0.67

    // Activity multiplier
    const activityMultipliers = {
      sedentary: 0.9,
      light: 1.0,
      moderate: 1.15,
      active: 1.3,
      athlete: 1.5
    }
    const activity = this.activityTarget.value
    baseOunces *= activityMultipliers[activity] || 1.0

    // Climate adjustment
    const climateAdj = {
      cold: -8,
      moderate: 0,
      warm: 16,
      hot: 32,
      altitude: 24
    }
    const climate = this.climateTarget.value
    baseOunces += climateAdj[climate] || 0

    // Exercise adjustment
    const exerciseDuration = parseInt(this.exerciseDurationTarget.value, 10) || 0
    const intensityMultipliers = {
      low: 0.5,
      moderate: 0.75,
      high: 1.0,
      extreme: 1.5
    }
    const intensity = this.exerciseIntensityTarget.value
    const exerciseOz = (exerciseDuration / 30) * 12 * (intensityMultipliers[intensity] || 0.75)
    baseOunces += exerciseOz

    // Additional factors
    if (this.caffeineTarget.checked) baseOunces += 16
    if (this.alcoholTarget.checked) baseOunces += 24
    if (this.pregnantTarget.checked) baseOunces += 24
    if (this.illnessTarget.checked) baseOunces += 32

    // Round and clamp
    const totalOunces = Math.round(Math.max(48, Math.min(200, baseOunces)))

    // Update displays
    this.updateMainDisplay(totalOunces)
    this.updateGlassDisplay(totalOunces)
    this.updateTimingRecommendations(totalOunces, exerciseDuration, intensity)
    this.updateExerciseHydration(exerciseDuration, intensity)
    this.updateInsight(totalOunces, activity, climate, exerciseDuration)
  }

  updateMainDisplay(ounces) {
    const liters = (ounces * 0.0295735).toFixed(1)

    if (this.isMetric) {
      this.totalOuncesTarget.textContent = (liters * 1000).toFixed(0)
      this.primaryUnitTarget.textContent = 'ml'
      this.alternateUnitTarget.textContent = `≈ ${liters} liters / ${ounces} oz`
    } else {
      this.totalOuncesTarget.textContent = ounces
      this.primaryUnitTarget.textContent = 'oz'
      this.alternateUnitTarget.textContent = `≈ ${liters} liters`
    }
  }

  updateGlassDisplay(ounces) {
    const glasses = ounces / 8
    const minGlasses = Math.floor(glasses)
    const maxGlasses = Math.ceil(glasses)

    this.glassCountTarget.textContent = minGlasses === maxGlasses ? minGlasses : `${minGlasses}-${maxGlasses}`

    // Visual representation (show 4 "glasses" as portions)
    const fillPercent = Math.min(100, (glasses / 16) * 100)
    const glassTargets = [this.glass1Target, this.glass2Target, this.glass3Target, this.glass4Target]

    glassTargets.forEach((glass, i) => {
      const glassPercent = Math.max(0, Math.min(100, (fillPercent - (i * 25)) * 4))
      const innerDiv = glass.querySelector('div')
      if (innerDiv) {
        innerDiv.style.height = `${glassPercent}%`
      }
    })
  }

  updateTimingRecommendations(totalOunces, exerciseDuration, _intensity) {
    // Morning hydration (start the day)
    const morningOz = Math.min(24, Math.round(totalOunces * 0.15))
    this.morningAmountTarget.textContent = `${morningOz} oz`

    // During exercise
    if (exerciseDuration > 0) {
      this.exerciseAmountTarget.textContent = '4-8 oz / 15 min'
    } else {
      this.exerciseAmountTarget.textContent = 'N/A'
    }

    // Hourly rate (assuming 16 waking hours)
    const hourlyOz = Math.round((totalOunces - morningOz) / 15)
    this.hourlyAmountTarget.textContent = `~${hourlyOz} oz / hour`
  }

  updateExerciseHydration(duration, intensity) {
    if (duration === 0) {
      this.preWorkoutTarget.textContent = 'N/A'
      this.duringWorkoutTarget.textContent = 'N/A'
      this.postWorkoutTarget.textContent = 'N/A'
      return
    }

    // Pre-workout
    this.preWorkoutTarget.textContent = '16-20 oz'

    // During workout (varies by intensity and duration)
    const duringBase = Math.round((duration / 60) * 24)
    const intensityMult = { low: 0.7, moderate: 1, high: 1.3, extreme: 1.6 }
    const duringOz = Math.round(duringBase * (intensityMult[intensity] || 1))
    const duringMin = Math.max(8, duringOz - 8)
    const duringMax = duringOz + 8
    this.duringWorkoutTarget.textContent = `${duringMin}-${duringMax} oz`

    // Post-workout
    const postMin = Math.round(duringOz * 0.7)
    const postMax = Math.round(duringOz * 1.1)
    this.postWorkoutTarget.textContent = `${Math.max(16, postMin)}-${Math.max(24, postMax)} oz`
  }

  updateInsight(totalOunces, activity, climate, exerciseDuration) {
    const parts = []

    // Base insight
    if (totalOunces < 64) {
      parts.push('Your hydration needs are relatively low.')
    } else if (totalOunces < 100) {
      parts.push('Your hydration needs are in the moderate range.')
    } else if (totalOunces < 140) {
      parts.push('Your hydration needs are above average.')
    } else {
      parts.push('Your hydration needs are high.')
    }

    // Activity-specific
    if (activity === 'athlete' || activity === 'active') {
      parts.push('Your active lifestyle requires consistent hydration throughout the day.')
    }

    // Climate-specific
    if (climate === 'hot' || climate === 'warm') {
      parts.push('Hot conditions increase fluid loss through sweating - prioritize electrolyte replacement.')
    } else if (climate === 'altitude') {
      parts.push('High altitude increases water loss through respiration - drink more than you think you need.')
    }

    // Exercise-specific
    if (exerciseDuration >= 90) {
      parts.push('For extended exercise sessions, consider sports drinks to replace electrolytes.')
    }

    // General tip
    parts.push('Spread intake throughout the day rather than drinking large amounts at once.')

    this.insightTarget.textContent = parts.join(' ')
  }
}
